import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getTokens, setTokens, clearTokens } from './api';

export interface AdminUser {
  id: string;
  email?: string;
  role: 'user' | 'artist' | 'admin';
  displayName?: string;
}

interface AuthCtx {
  user: AdminUser | null;
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void (async () => {
      if (!getTokens()) {
        setReady(true);
        return;
      }
      try {
        const me = await api<AdminUser>('/api/v1/me');
        if (me.role === 'admin') setUser(me);
        else clearTokens();
      } catch {
        clearTokens();
      }
      setReady(true);
    })();
  }, []);

  const login = async (email: string, password: string) => {
    const out = await api<{ user: AdminUser; tokens: { accessToken: string; refreshToken: string } }>(
      '/api/v1/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }), auth: false },
    );
    if (out.user.role !== 'admin') {
      clearTokens();
      throw new Error('Not an admin account');
    }
    setTokens(out.tokens);
    setUser(out.user);
  };
  const logout = () => {
    clearTokens();
    setUser(null);
  };

  return <Ctx.Provider value={{ user, ready, login, logout }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth outside provider');
  return c;
}
